import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Check } from "lucide-react";
import { ROADMAP } from "@/constants/content";
import { HOME } from "@/constants/testIds";

const Roadmap = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="roadmap" className="section-pad relative">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{ background: "linear-gradient(180deg, #ffffff, #f5f7fa)" }}
      />
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="chip">Complete Marathon Roadmap</div>
          <h2
            className="mt-3 font-display font-bold tracking-tight text-slate-900"
            style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}
          >
            Every step from an empty screen to your <span className="gradient-text-emerald">first sale</span>.
          </h2>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.35fr] items-start">
          {/* Vertical timeline (left column pinned) */}
          <div className="relative">
            <div className="absolute left-6 sm:left-7 top-3 bottom-3 w-px bg-gradient-to-b from-emerald-500/40 via-emerald-500/30 to-lime-400/30" />
            <ul className="space-y-3">
              {ROADMAP.map((m, i) => {
                const active = i === openIdx;
                return (
                  <li key={m.phase}>
                    <button
                      onClick={() => setOpenIdx(i)}
                      data-testid={`${HOME.roadmapModule}-${i}`}
                      className={`w-full text-left flex items-center gap-4 pl-2 pr-3 py-3 rounded-2xl border transition-all ${
                        active
                          ? "bg-white border-emerald-500/30 shadow-premium"
                          : "bg-white/50 border-transparent hover:border-slate-200"
                      }`}
                    >
                      <div
                        className={`relative w-12 h-12 shrink-0 rounded-2xl grid place-items-center font-display font-bold text-sm ${
                          active
                            ? "bg-gradient-to-br from-emerald-600 to-emerald-500 text-white shadow-emerald-glow"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {m.phase}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-700">Phase {i + 1}</div>
                        <div className="font-display font-semibold text-slate-900 truncate">{m.title}</div>
                      </div>
                      <div className="ml-auto">
                        {active ? (
                          <Minus className="w-4 h-4 text-emerald-700" />
                        ) : (
                          <Plus className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Detail card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={openIdx}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="relative rounded-3xl glass-light border border-white/70 shadow-premium p-6 sm:p-8 overflow-hidden"
            >
              <div className="aurora opacity-40" aria-hidden />
              <div className="relative z-10">
                <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-700">
                  {ROADMAP[openIdx].phase} · Module {openIdx + 1} of {ROADMAP.length}
                </div>
                <h3 className="mt-2 font-display font-bold text-slate-900" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
                  {ROADMAP[openIdx].title}
                </h3>
                <p className="mt-3 text-slate-600 max-w-xl">{ROADMAP[openIdx].summary}</p>

                <ul className="mt-6 grid sm:grid-cols-2 gap-2">
                  {ROADMAP[openIdx].items.map((it) => (
                    <li key={it} className="flex items-center gap-2 rounded-xl bg-white/70 border border-slate-100 px-3 py-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Roadmap;
